<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { ElDialog, ElButton, ElInput, ElSpace } from "element-plus";
import NoteMenuState from "@/stores/NoteMenuState";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import LegendLib from "@/utils/LegendLib";
import type { NoteConfig } from "@/types/legend.types";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_MODAL } from "@/components/canvas/layouts/constants";

const CRACK_NUMBERS = [
  "0.1",
  "0.2",
  "0.3",
  "0.4",
  "0.5",
  "0.6",
  "0.7",
  "0.8",
  "0.9",
  "1.0",
  "1.1",
  "1.2",
  "1.3",
  "1.4",
  "1.5",
  "1.6",
  "1.7",
  "1.8",
  "1.9",
];

const NUMBERS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const UNIT_SYMBOLS = ["μ", "mm", "cm", "m", "m2", "m3"];

const props = defineProps<{
  visible: boolean;
  message: string;
  multiline?: boolean;
}>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
  update: [value: string];
}>();

const isVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

const localMessage = ref(props.message);

watch(
  () => props.visible,
  (val) => {
    if (val) localMessage.value = props.message;
  },
);

function getInputElement() {
  return (inputRef.value?.input ?? inputRef.value?.textarea) as
    | HTMLInputElement
    | HTMLTextAreaElement
    | undefined;
}

/**
 * Replace le curseur après une insertion par bouton — SANS redonner le focus
 * au champ. Sur tablette, un `focus()` explicite rouvrait le clavier natif à
 * chaque bouton pressé (espace, unité, numéro de fissure…) alors que
 * l'utilisateur venait justement de le fermer pour atteindre ces boutons. Le
 * focus est conservé par `@pointerdown.prevent` sur les boutons ; s'il a été
 * perdu malgré tout, `cursorPos` garde la position pour la prochaine insertion.
 */
function restoreCursor() {
  nextTick(() => {
    const el = getInputElement();
    if (el && document.activeElement === el) {
      el.setSelectionRange(cursorPos.value, cursorPos.value);
    }
  });
}

function deleteChars(numChar = 1) {
  const pos = cursorPos.value;
  const current = localMessage.value;
  const deleteFrom = Math.max(0, pos - numChar);
  localMessage.value = current.slice(0, deleteFrom) + current.slice(pos);
  cursorPos.value = deleteFrom;
  restoreCursor();
}

function addToInput(text: string) {
  const pos = cursorPos.value;
  const current = localMessage.value;
  localMessage.value = current.slice(0, pos) + text + current.slice(pos);
  cursorPos.value = pos + text.length;
  restoreCursor();
}

function validate() {
  emit("update", localMessage.value);
  isVisible.value = false;
}

function getBubbleColor(config: NoteConfig): string {
  const legend = CanvasSettingsState.activeLegend;
  if (!legend) return "#888888";
  return LegendLib.getComputedColor(config, legend);
}

// category.noteIds est l'ordre de référence pour chaque catalogue (indépendant d'un
// catalogue à l'autre) : on résout les ids vers leurs notes plutôt que de filtrer les
// notes par appartenance, ce qui donnerait l'ordre du tableau global, pas celui du catalogue.
const catalogues = computed<Map<string, NoteConfig[]>>(() => {
  const map = new Map<string, NoteConfig[]>();
  const active = NoteMenuState.activeCategories;
  const legendCategories = CanvasSettingsState.activeLegend?.categories ?? [];
  const notesById = new Map(NoteMenuState.noteConfigs.map((n) => [n.id, n]));

  for (const category of legendCategories) {
    if (active.length && !active.includes(category.name)) continue;
    const categoryNotes = category.noteIds
      .map((id) => notesById.get(id))
      .filter((n): n is NoteConfig => !!n);
    if (categoryNotes.length > 0) map.set(category.name, categoryNotes);
  }
  return map;
});

const inputRef = ref<InstanceType<typeof ElInput> | null>(null);
const cursorPos = ref(0);
const isMultilineLocal = ref(props.multiline ?? false);

watch(
  () => props.multiline,
  (val) => {
    isMultilineLocal.value = val ?? false;
  },
);

function saveCursor() {
  const el = getInputElement();
  if (el) cursorPos.value = el.selectionStart ?? localMessage.value.length;
}

watch(isVisible, (val) => {
  if (val) cursorPos.value = localMessage.value.length;
});

const activeTab = ref<string>("");
watch(
  catalogues,
  (newCatalogues) => {
    if (!newCatalogues.has(activeTab.value)) {
      activeTab.value = [...newCatalogues.keys()][0] ?? "";
    }
  },
  { immediate: true },
);
</script>
<template>
  <ElDialog
    v-model="isVisible"
    title="Edit Text"
    class="canvas-dialog"
    width="90%"
    :close-on-click-modal="false"
    append-to-body
    :z-index="Z_INDEX_MODAL"
  >
    <div class="keyboard-modal-content">
      <!-- Input Section -->
      <div class="input-section">
        <div style="display: flex; gap: 10px; width: 100%">
          <ElInput
            ref="inputRef"
            v-model="localMessage"
            :type="multiline ? 'textarea' : 'text'"
            :rows="multiline ? 4 : undefined"
            placeholder="Enter text"
            clearable
            style="flex: 1; min-width: 0"
            autofocus
            @blur="saveCursor"
            @keyup="saveCursor"
            @click="saveCursor"
          />
          <ElButton type="primary" @click="validate()">OK</ElButton>
        </div>
      </div>

      <!-- Special Characters -->
      <div class="button-section">
        <ElSpace wrap>
          <ElButton
            v-if="multiline"
            @pointerdown.prevent
            @click="addToInput('\n')"
            >↵</ElButton
          >
          <ElButton @pointerdown.prevent @click="addToInput(' ')"
            >Space</ElButton
          >
          <ElButton @pointerdown.prevent @click="addToInput('+')">+</ElButton>
          <ElButton @pointerdown.prevent @click="addToInput('/')">/</ElButton>
          <ElButton @pointerdown.prevent @click="addToInput('(')">(</ElButton>
          <ElButton @pointerdown.prevent @click="addToInput(')')">)</ElButton>
          <ElButton @pointerdown.prevent @click="addToInput('.')">.</ElButton>
          <ElButton @pointerdown.prevent @click="addToInput('-')">-</ElButton>
          <ElButton @pointerdown.prevent @click="deleteChars()">
            <PhosphorIcon name="backspace" :size="16" />
          </ElButton>
        </ElSpace>
      </div>

      <!-- Note Configs -->
      <div v-if="catalogues.size" class="button-section">
        <div class="note-tabs-header">
          <button
            v-for="[name] in catalogues.entries()"
            :key="name"
            class="note-tab-btn"
            :class="{ active: activeTab === name }"
            @pointerdown.prevent
            @click="activeTab = name"
          >
            {{ name }}
          </button>
        </div>
        <div class="note-configs-grid">
          <button
            v-for="config in catalogues.get(activeTab) ?? []"
            :key="config.name"
            class="note-config-btn"
            @pointerdown.prevent
            @click="addToInput(config.label || config.name)"
          >
            <span
              class="color-bubble"
              :style="{ backgroundColor: getBubbleColor(config) }"
            ></span>
            {{ config.label || config.name }}
          </button>
        </div>
      </div>

      <!-- Numbers -->
      <div class="button-section">
        <h6>Numbers</h6>
        <ElSpace wrap>
          <ElButton
            v-for="num in NUMBERS"
            :key="num"
            @pointerdown.prevent
            @click="addToInput(num)"
            size="small"
          >
            {{ num }}
          </ElButton>
        </ElSpace>
      </div>

      <!-- Crack Numbers -->
      <div class="button-section">
        <h6>Crack Numbers</h6>
        <ElSpace wrap>
          <ElButton
            v-for="crack in CRACK_NUMBERS"
            :key="crack"
            @pointerdown.prevent
            @click="addToInput(crack)"
            size="small"
          >
            {{ crack }}
          </ElButton>
        </ElSpace>
      </div>

      <!-- Units -->
      <div class="button-section">
        <h6>Units</h6>
        <ElSpace wrap>
          <ElButton
            v-for="unit in UNIT_SYMBOLS"
            :key="unit"
            @pointerdown.prevent
            @click="addToInput(unit)"
            size="small"
          >
            {{ unit }}
          </ElButton>
        </ElSpace>
      </div>
    </div>
  </ElDialog>
</template>

<style scoped>
.keyboard-modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-section {
  position: sticky;
  top: 0;
  background: var(--canvas-bg);
  z-index: 1;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--canvas-border);
}

.button-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.button-section h6 {
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.note-tabs-header {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-bottom: 2px solid var(--canvas-border);
  margin-bottom: 2px;
}

.note-tab-btn {
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  background: transparent;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  transition:
    color 0.15s,
    border-color 0.15s;
  white-space: nowrap;
}

/* Survols à la souris seulement : au stylet, :hover reste collé sur la
   dernière touche appuyée, qui restait bleue. */
@media (hover: hover) and (pointer: fine) {
  .note-tab-btn:hover {
    color: var(--el-color-primary);
  }
}

.note-tab-btn.active {
  color: var(--el-color-primary);
  border-bottom-color: var(--el-color-primary);
}

.note-configs-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.note-config-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  border: 1px solid var(--canvas-border);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  transition:
    border-color 0.15s,
    background 0.15s;
  white-space: nowrap;
}

@media (hover: hover) and (pointer: fine) {
  .note-config-btn:hover {
    border-color: var(--el-color-primary);
    color: var(--el-color-primary);
  }
}

.color-bubble {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
